import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Input, Textarea, Select, Card } from "../components/ui";

const TIPO_UTENZA_OPTIONS = [
  { value: "100000000", label: "Energia elettrica" },
  { value: "100000001", label: "Gas naturale" },
  { value: "100000002", label: "Acqua" },
  { value: "100000003", label: "Teleriscaldamento" },
  { value: "100000004", label: "Telecomunicazioni" },
  { value: "100000005", label: "Rifiuti" },
  { value: "100000006", label: "Altro" },
];

const STATO_PAGAMENTO_OPTIONS = [
  { value: "100000000", label: "Da pagare" },
  { value: "100000001", label: "Pagata" },
  { value: "100000002", label: "Scaduta" },
  { value: "100000003", label: "In contestazione" },
];

function NuovoConsumoPage() {
  const { id: editId } = useParams();
  const isEditing = !!editId;
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [rsaList, setRsaList] = useState([]);
  const [immobiliList, setImmobiliList] = useState([]);
  const [contatoriList, setContatoriList] = useState([]);
  const [fornitoriList, setFornitoriList] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    tipoutenza: "",
    periodoInizio: "",
    periodoFine: "",
    dataEmissione: "",
    dataScadenza: "",
    importoTotale: "",
    importoEnergia: "",
    importoRete: "",
    importoImposte: "",
    consumoQuantita: "",
    consumoUnita: "",
    pod: "",
    numeroFattura: "",
    statoPagamento: "100000000",
    note: "",
    rsaId: searchParams.get("rsaId") || "",
    immobileId: searchParams.get("immobileId") || "",
    contatoreId: "",
    fornitoreId: "",
  });

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rsaData, fornitoriData] = await Promise.all([
          apiRequest("/rsa"),
          apiRequest("/fornitori").catch(() => ({ value: [] })),
        ]);
        setRsaList(rsaData.value || []);
        setFornitoriList(fornitoriData.value || []);

        // If editing, load existing data
        if (isEditing) {
          const consumo = await apiRequest(`/consumi-utenze/${editId}`);
          setFormData({
            tipoutenza:
              consumo.tipoutenza !== null ? String(consumo.tipoutenza) : "",
            periodoInizio: consumo.periodoInizio?.split("T")[0] || "",
            periodoFine: consumo.periodoFine?.split("T")[0] || "",
            dataEmissione: consumo.dataEmissione?.split("T")[0] || "",
            dataScadenza: consumo.dataScadenza?.split("T")[0] || "",
            importoTotale: consumo.importoTotale ?? "",
            importoEnergia: consumo.importoEnergia ?? "",
            importoRete: consumo.importoRete ?? "",
            importoImposte: consumo.importoImposte ?? "",
            consumoQuantita: consumo.consumoQuantita ?? "",
            consumoUnita: consumo.consumoUnita || "",
            pod: consumo.pod || "",
            numeroFattura: consumo.numeroFattura || "",
            statoPagamento:
              consumo.statoPagamento !== null
                ? String(consumo.statoPagamento)
                : "100000000",
            note: consumo.note || "",
            rsaId: consumo.rsaId || "",
            immobileId: consumo.immobileId || "",
            contatoreId: consumo.contatoreId || "",
            fornitoreId: consumo.fornitoreId || "",
          });
        }
      } catch (error) {
        console.error("Error loading data:", error);
        showToast("Errore nel caricamento dei dati", "error");
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [apiRequest, editId, isEditing, showToast, searchParams]);

  // Load immobili when RSA changes
  useEffect(() => {
    if (!formData.rsaId) {
      setImmobiliList([]);
      setContatoriList([]);
      return;
    }

    const fetchImmobili = async () => {
      try {
        const data = await apiRequest(`/immobili?rsaId=${formData.rsaId}`);
        setImmobiliList(data.value || []);
      } catch {
        setImmobiliList([]);
      }
    };

    const fetchContatori = async () => {
      try {
        const data = await apiRequest(`/contatori?rsaId=${formData.rsaId}`);
        setContatoriList(data || []);
      } catch {
        setContatoriList([]);
      }
    };

    fetchImmobili();
    fetchContatori();
  }, [formData.rsaId, apiRequest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.rsaId) newErrors.rsaId = "La RSA è obbligatoria";
    if (!formData.fornitoreId)
      newErrors.fornitoreId = "Il fornitore è obbligatorio";
    if (!formData.importoTotale && formData.importoTotale !== 0)
      newErrors.importoTotale = "L'importo totale è obbligatorio";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast("Correggi gli errori nel form", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        tipoutenza: formData.tipoutenza ? parseInt(formData.tipoutenza) : null,
        periodoInizio: formData.periodoInizio || null,
        periodoFine: formData.periodoFine || null,
        dataEmissione: formData.dataEmissione || null,
        dataScadenza: formData.dataScadenza || null,
        importoTotale: formData.importoTotale
          ? parseFloat(formData.importoTotale)
          : null,
        importoEnergia: formData.importoEnergia
          ? parseFloat(formData.importoEnergia)
          : null,
        importoRete: formData.importoRete
          ? parseFloat(formData.importoRete)
          : null,
        importoImposte: formData.importoImposte
          ? parseFloat(formData.importoImposte)
          : null,
        consumoQuantita: formData.consumoQuantita
          ? parseFloat(formData.consumoQuantita)
          : null,
        consumoUnita: formData.consumoUnita || null,
        pod: formData.pod || null,
        numeroFattura: formData.numeroFattura || null,
        statoPagamento: parseInt(formData.statoPagamento),
        note: formData.note || null,
        rsaId: formData.rsaId,
        immobileId: formData.immobileId || null,
        contatoreId: formData.contatoreId || null,
        fornitoreId: formData.fornitoreId,
      };

      if (isEditing) {
        await apiRequest(`/consumi-utenze/${editId}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        showToast("Bolletta aggiornata con successo", "success");
        navigate(`/consumi-utenze/${editId}`);
      } else {
        const result = await apiRequest("/consumi-utenze", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        showToast("Bolletta creata con successo", "success");
        navigate(`/consumi-utenze/${result.id}`);
      }
    } catch (error) {
      console.error("Error saving consumo:", error);
      showToast("Errore nel salvataggio", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <a
          href="/consumi-utenze"
          className="hover:text-blue-600"
          onClick={(e) => {
            e.preventDefault();
            navigate("/consumi-utenze");
          }}
        >
          Bollette e Consumi
        </a>
        <span>/</span>
        <span className="text-gray-900">
          {isEditing ? "Modifica Bolletta" : "Nuova Bolletta"}
        </span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900">
        {isEditing ? "Modifica Bolletta" : "Nuova Bolletta"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Collegamento */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Collegamento</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="RSA *"
              name="rsaId"
              value={formData.rsaId}
              onChange={handleChange}
              error={errors.rsaId}
              options={[
                { value: "", label: "Seleziona RSA" },
                ...rsaList.map((r) => ({ value: r.id, label: r.nome })),
              ]}
            />
            <Select
              label="Fornitore *"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleChange}
              error={errors.fornitoreId}
              options={[
                { value: "", label: "Seleziona fornitore" },
                ...fornitoriList.map((f) => ({
                  value: f.id,
                  label: f.nome,
                })),
              ]}
            />
            <Select
              label="Immobile"
              name="immobileId"
              value={formData.immobileId}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona immobile (opzionale)" },
                ...immobiliList.map((i) => ({
                  value: i.id,
                  label: i.nome,
                })),
              ]}
              disabled={!formData.rsaId}
            />
            <Select
              label="Contatore (POD/PDR)"
              name="contatoreId"
              value={formData.contatoreId}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona contatore (opzionale)" },
                ...contatoriList.map((c) => ({
                  value: c.id,
                  label: `${c.codice} - ${c.tipoutenzaLabel || ""}`,
                })),
              ]}
              disabled={!formData.rsaId}
            />
          </div>
        </Card>

        {/* Tipo e Stato */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Tipo e Stato</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Tipo Utenza"
              name="tipoutenza"
              value={formData.tipoutenza}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona tipo" },
                ...TIPO_UTENZA_OPTIONS,
              ]}
            />
            <Select
              label="Stato Pagamento"
              name="statoPagamento"
              value={formData.statoPagamento}
              onChange={handleChange}
              options={STATO_PAGAMENTO_OPTIONS}
            />
            <Input
              label="POD/PDR"
              name="pod"
              value={formData.pod}
              onChange={handleChange}
              placeholder="es. IT001E00012345"
            />
          </div>
        </Card>

        {/* Importi */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Importi</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              label="Importo Totale *"
              name="importoTotale"
              type="number"
              step="0.01"
              value={formData.importoTotale}
              onChange={handleChange}
              error={errors.importoTotale}
              placeholder="0.00"
            />
            <Input
              label="Materia Prima"
              name="importoEnergia"
              type="number"
              step="0.01"
              value={formData.importoEnergia}
              onChange={handleChange}
              placeholder="0.00"
            />
            <Input
              label="Rete/Trasporto"
              name="importoRete"
              type="number"
              step="0.01"
              value={formData.importoRete}
              onChange={handleChange}
              placeholder="0.00"
            />
            <Input
              label="Imposte"
              name="importoImposte"
              type="number"
              step="0.01"
              value={formData.importoImposte}
              onChange={handleChange}
              placeholder="0.00"
            />
          </div>
        </Card>

        {/* Consumi e Periodo */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Consumi e Periodo</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Consumo"
              name="consumoQuantita"
              type="number"
              step="0.01"
              value={formData.consumoQuantita}
              onChange={handleChange}
              placeholder="es. 1500"
            />
            <Input
              label="Unità di misura"
              name="consumoUnita"
              value={formData.consumoUnita}
              onChange={handleChange}
              placeholder="es. kWh, Smc, m3"
            />
            <Input
              label="Periodo Inizio"
              name="periodoInizio"
              type="date"
              value={formData.periodoInizio}
              onChange={handleChange}
            />
            <Input
              label="Periodo Fine"
              name="periodoFine"
              type="date"
              value={formData.periodoFine}
              onChange={handleChange}
            />
          </div>
        </Card>

        {/* Fattura */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Dati Fattura</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Numero Fattura"
              name="numeroFattura"
              value={formData.numeroFattura}
              onChange={handleChange}
              placeholder="es. FA-2026/0001"
            />
            <Input
              label="Data Emissione"
              name="dataEmissione"
              type="date"
              value={formData.dataEmissione}
              onChange={handleChange}
            />
            <Input
              label="Data Scadenza"
              name="dataScadenza"
              type="date"
              value={formData.dataScadenza}
              onChange={handleChange}
            />
          </div>
        </Card>

        {/* Note */}
        <Card className="p-6">
          <Textarea
            label="Note"
            name="note"
            value={formData.note}
            onChange={handleChange}
            rows={3}
            placeholder="Note aggiuntive..."
          />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            type="button"
            onClick={() =>
              navigate(
                isEditing ? `/consumi-utenze/${editId}` : "/consumi-utenze",
              )
            }
          >
            Annulla
          </Button>
          <Button type="submit" disabled={loading}>
            {loading
              ? "Salvataggio..."
              : isEditing
                ? "Salva Modifiche"
                : "Crea Bolletta"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NuovoConsumoPage;
